import {
  Base,
  BaseProps,
  Button,
  DatePicker,
  DropdownMenu,
  DropdownMenuItem,
  Icon,
  Placement,
  PlacementManager,
  PlacementReference,
  Text,
} from '@bitrise/bitkit';
import { DateTime } from 'luxon';
import React, { useContext, useState, FunctionComponent } from 'react';
import { TIME_RANGES, TypeDuration } from '../../constants/TimeRange';
import dataAtIds from '../../dataAtIds.json';
import getDateRange from '../../utils/time/getTimeRange';
import { trackEvent } from '../../utils/tracking';
import { URLStateContext } from '../URLState/URLState';

interface Props extends BaseProps {
  onChange?: () => void;
}

type TimeRangeOptionAtIdKeys =
  | 'TimeRangeOptionPT24H'
  | 'TimeRangeOptionPT48H'
  | 'TimeRangeOptionP7D'
  | 'TimeRangeOptionP30D'
  | 'TimeRangeOptionP6M';

const TimeRange: FunctionComponent<Props> = (props) => {
  const { onChange, ...rest } = props;
  const { dateFrom, dateTo, duration, updateUrlState } = useContext(URLStateContext);
  const { dateFrom: start, dateTo: end, text } = getDateRange(duration, dateFrom, dateTo);
  const [visibleDatePicker, setVisibleDatePicker] = useState(false);
  const [visibleDropdown, setVisibleDropdown] = useState(false);

  const handleApplyCustomRange = (
    newDateFrom?: number,
    newDateTo: undefined | number = newDateFrom,
  ) => {
    if (newDateFrom) {
      const dateRange = getDateRange('FIXED', newDateFrom, newDateTo);

      trackEvent('TimeRange.Custom', {
        dateFrom: DateTime.fromMillis(dateRange.dateFrom).toISODate(),
        dateTo: DateTime.fromMillis(dateRange.dateTo).toISODate(),
      });

      updateUrlState({
        dateFrom: DateTime.fromMillis(dateRange.dateFrom).toMillis(),
        dateTo: DateTime.fromMillis(dateRange.dateTo).toMillis(),
        duration: 'FIXED',
      });

      if (typeof onChange === 'function') {
        onChange();
      }
    }
  };

  const handleSelectDuration = (newDuration: TypeDuration) => {
    trackEvent('TimeRange.Predefined', { duration: newDuration });
    setVisibleDropdown(false);
    updateUrlState({
      dateFrom: undefined,
      dateTo: undefined,
      duration: newDuration,
    });

    if (typeof onChange === 'function') {
      onChange();
    }
  };

  const handleToggleDropdown = () => setVisibleDropdown(!visibleDropdown);
  const handleHideDatePicker = () => setVisibleDatePicker(false);
  const handleShowDatePicker = () => {
    setVisibleDatePicker(true);
    setVisibleDropdown(false);
  };

  return (
    <DatePicker
      dateSelectableFrom={DateTime.local().minus({ months: 3 }).toMillis()}
      dateSelectableTo={DateTime.local().toMillis()}
      dateSelectedFrom={start}
      dateSelectedTo={end}
      onApply={handleApplyCustomRange}
      onClose={handleHideDatePicker}
      visible={visibleDatePicker}
    >
      {({ ref: refDatePicker }) => (
        <PlacementManager>
          <PlacementReference>
            {({ ref: refDropdown }) => (
              <Base {...rest} innerRef={refDatePicker}>
                <Button
                  alignChildrenHorizontal="start"
                  data-trace-at-id={dataAtIds.TimeRangeButton}
                  innerRef={refDropdown}
                  level="tertiary"
                  onClick={handleToggleDropdown}
                >
                  <Icon name="Calendar" />
                  <Text>{text}</Text>
                  <Icon name="DropdownArrows" />
                </Button>
              </Base>
            )}
          </PlacementReference>

          <Placement onClose={handleToggleDropdown} visible={visibleDropdown}>
            {({ width }) => (
              <DropdownMenu minWidth={width}>
                {TIME_RANGES.filter(({ selectable }) => selectable).map(
                  ({ text: timeRangeText, value }) => (
                    <DropdownMenuItem
                      data-trace-at-id={
                        dataAtIds[`TimeRangeOption${value}` as TimeRangeOptionAtIdKeys]
                      }
                      key={timeRangeText}
                      onClick={() => handleSelectDuration(value)}
                      selected={duration === value}
                    >
                      {timeRangeText}
                    </DropdownMenuItem>
                  ),
                )}

                <DropdownMenuItem
                  data-trace-at-id={dataAtIds.TimeRangeOptionCustom}
                  onClick={handleShowDatePicker}
                >
                  Custom...
                </DropdownMenuItem>
              </DropdownMenu>
            )}
          </Placement>
        </PlacementManager>
      )}
    </DatePicker>
  );
};

export default TimeRange;
